import React from 'react';
import { Download, Loader2, Save } from 'lucide-react';
import { Button } from '@daater/ui';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import DataTable from './data-table';

type DataSectionProps<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  loading?: boolean;
  totalItems?: number;
  title?: string;
  cellMaxWidth?: number;
  onDownload?: () => void;
  isDownloading?: boolean;
  downloadDisabled?: boolean;
  onSave?: (reportName: string) => Promise<boolean | void> | boolean | void;
  saveError?: string | null;
  saveSuccessMessage?: string | null;
  isSaving?: boolean;
  saveDisabled?: boolean;
  saved?: boolean;
  initialColumnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (state: VisibilityState) => void;
  onFilteredDataChange?: (rows: T[]) => void;
};

function DataSection<T>({
  data,
  columns,
  loading,
  totalItems,
  title = 'Tabla de datos',
  cellMaxWidth = 260,
  onDownload,
  isDownloading = false,
  downloadDisabled = false,
  onSave,
  saveError,
  saveSuccessMessage,
  isSaving = false,
  saveDisabled = false,
  saved = false,
  initialColumnVisibility,
  onColumnVisibilityChange,
  onFilteredDataChange,
}: DataSectionProps<T>) {
  const [saveOpen, setSaveOpen] = React.useState(false);
  const [reportName, setReportName] = React.useState('');

  const handleSave = async () => {
    if (!onSave) return;
    const result = await onSave(reportName.trim());
    if (result === false) return;
    setSaveOpen(false);
    setReportName('');
  };

  const isSaveButtonDisabled = saveDisabled || isSaving || !onSave;
  const isSaveConfirmDisabled = isSaveButtonDisabled;

  return (
    <section className="border-y border-pale-grey-three bg-white sm:rounded-xl sm:border sm:shadow-lg">
      <div className="border-b border-pale-grey-three px-3 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-charcoal-grey">{title}</h2>
            <p className="text-sm text-slate">
              {data.length.toLocaleString('es-CO')} filas cargadas
              {typeof totalItems === 'number'
                ? ` • ${totalItems.toLocaleString('es-CO')} totales`
                : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDownload}
              disabled={downloadDisabled || isDownloading}
            >
              {isDownloading ? (
                <span className="mr-2 inline-flex h-4 w-4 animate-spin rounded-full border-2 border-primary/40 border-t-primary" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {isDownloading ? 'Generando...' : 'Descargar'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSaveOpen(prev => !prev)}
              disabled={isSaveButtonDisabled}
            >
              <Save className="mr-2 h-4 w-4" />
              {saved ? 'Guardado' : 'Guardar'}
            </Button>
          </div>
        </div>
        {saveSuccessMessage ? (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {saveSuccessMessage}
          </div>
        ) : null}
        {saveOpen ? (
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="flex-1">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate">
                  Nombre del reporte
                </span>
                <input
                  type="text"
                  value={reportName}
                  onChange={event => setReportName(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleSave();
                    }
                  }}
                  className="w-full rounded-lg border border-pale-grey-three px-3 py-2 text-sm text-charcoal-grey shadow-sm focus:border-primary focus:ring focus:ring-primary/20"
                  placeholder="Escribe un nombre para guardar esta búsqueda"
                />
              </label>
              {saveError ? (
                <div className="mt-2 rounded-lg border border-darkish-pink/20 bg-darkish-pink/5 px-3 py-2 text-sm text-darkish-pink">
                  {saveError}
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setSaveOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => void handleSave()}
                disabled={isSaveConfirmDisabled}
              >
                {isSaving ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="analysis-loading-pulse flex items-center justify-center gap-2 px-6 py-10 text-sm text-slate">
          <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
          Cargando registros...
        </div>
      ) : data.length ? (
        <div className="px-2 py-4 sm:px-6 sm:py-6">
          <DataTable
            data={data}
            columns={columns}
            cellMaxWidth={cellMaxWidth}
            initialColumnVisibility={initialColumnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            onFilteredDataChange={onFilteredDataChange}
          />
        </div>
      ) : (
        <div className="px-6 py-10 text-sm text-slate">
          No hay datos disponibles para esta búsqueda.
        </div>
      )}
    </section>
  );
}

export default DataSection;
